// Shared IP-based geolocation used by both language auto-selection and currency conversion.
// NOTE: 'https://freeipapi.com/api/json/' 302-redirects here, but the redirect response
// itself has no Access-Control-Allow-Origin header, which makes browser fetch() fail the
// CORS check on that hop even though this final destination is CORS-enabled. Calling it
// directly avoids the broken redirect.
const GEO_ENDPOINT = 'https://free.freeipapi.com/api/json/';
const CACHE_KEY = 'rs_geo_country';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.countryCode !== 'string') return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.countryCode;
  } catch (e) {
    return null;
  }
}

function writeCache(countryCode) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ countryCode, timestamp: Date.now() }));
  } catch (e) {
    // Ignore storage failures (private browsing, quota, etc.)
  }
}

let inFlightPromise = null;

/**
 * Resolves the visitor's ISO-3166 alpha-2 country code via freeipapi.com.
 * Result is cached (localStorage + in-memory in-flight dedupe) so the
 * network call only happens once per day per browser, shared across
 * language detection and currency conversion.
 */
export async function detectCountryCode() {
  if (typeof window === 'undefined') return null;

  const cached = readCache();
  if (cached) return cached;

  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const response = await fetch(GEO_ENDPOINT, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn(`[geo] freeipapi responded with ${response.status}; defaulting to USD/browser locale.`);
        return null;
      }

      const data = await response.json();
      const countryCode = (
        data?.countryCode ||
        data?.country_code ||
        data?.country ||
        ''
      )
        .toString()
        .toUpperCase();

      if (!countryCode || countryCode.length !== 2) {
        console.warn('[geo] freeipapi response had no usable countryCode field:', data);
        return null;
      }

      writeCache(countryCode);
      return countryCode;
    } catch (e) {
      console.warn('[geo] IP geolocation lookup failed (likely blocked by an ad-blocker/extension or network), falling back to browser defaults:', e);
      return null;
    }
  })();

  try {
    return await inFlightPromise;
  } finally {
    inFlightPromise = null;
  }
}

export default detectCountryCode;
